import React, { useEffect, useRef, useState } from "react";
import { breakpoints, useMediaQuery } from "@tvg/design-system";
import useWagerProfile from "@tvg/custom-hooks/hooks/useWagerProfile";
import { useQuery } from "@apollo/client";
import { RaceTypeCodeEnum } from "@tvg/ts-types/Race";
import { events as AlchemerEvents } from "@urp/alchemer";
import { GET_INLINE_PAST_PERFORMANCE } from "../../../../graphql/queries/InlinePastPerformance";
import queryHelper from "../../../../graphql/options.graph";
import { ApolloResponsePastRaces, Props } from "./types";
import DesktopRunnerInfo from "./Desktop";
import MobileRunnerInfo from "./Mobile";

const RUNNER_INFO_HEIGHT_ANIMATION_TIME = 250;

const RunnerInfo = ({
  isOpen,
  runnerBI,
  handicapping,
  timeform,
  trackDataSource,
  currentRace,
  entityRunnerId,
  runnerNumber,
  runnerName,
  runnerDob,
  fcpClient,
  changes,
  isWPSBetType,
  bestPerformanceRunners,
  hasSilk,
  children
}: Props) => {
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const [selectedRacePP, setSelectedRacePP] = useState(-1);
  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const wagerProfile = useWagerProfile();
  const { data, loading } = useQuery<ApolloResponsePastRaces>(
    GET_INLINE_PAST_PERFORMANCE,
    queryHelper.apolloOptionsInlinePP({
      wagerProfile,
      isOpen,
      entityRunnerId,
      fcpClient
      // limit: 5, // Commented until backend is ready to receive all the filters
    })
  );
  const pastRaces = data ? data.pastRaces : [];

  const emptyComments =
    (handicapping?.freePick?.info ?? null) === null &&
    (timeform?.analystsComments ?? null) === null;

  const emptyCommentsHandicapping =
    emptyComments ||
    ((handicapping?.freePick?.info ?? "") === "" &&
      (timeform?.analystsComments ?? "") === "");

  const isDriver = currentRace?.type?.code === RaceTypeCodeEnum.HARNESS;

  const handleSelectRace = (index: number) => {
    AlchemerEvents.selectInlinePastPerformance();
    setSelectedRacePP(index);
  };

  // Height must be set with the state because the ref doesn't trigger a re-render in the component
  // so when the component is opened the loading is true and ref scrollHeight is X (loading's height)
  // and when the loading is finished, the scrollHeight ix Y (content's height) but there is nothing
  // telling the component to re-render, so it's stuck until the next render
  // This can be reproduced by passing the ref.current.scrollHeight directly into the contentHeight prop on Container
  useEffect(() => {
    if (isOpen && ref && ref.current) {
      setHeight(ref.current.scrollHeight);
    }
  }, [loading, isOpen]);

  useEffect(() => {
    if (!loading && isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest"
        });
      }, RUNNER_INFO_HEIGHT_ANIMATION_TIME);
    }
  }, [loading, isOpen]);

  const UIComponent = isMobile ? MobileRunnerInfo : DesktopRunnerInfo;

  return (
    <UIComponent
      entityRunnerId={entityRunnerId}
      runnerNumber={runnerNumber}
      runnerName={runnerName}
      runnerBI={runnerBI}
      bestPerformanceRunners={bestPerformanceRunners}
      isOpen={isOpen}
      runnerDob={runnerDob}
      selectedRacePP={selectedRacePP}
      currentRace={currentRace}
      setSelectedRacePP={setSelectedRacePP}
      pastRaces={pastRaces}
      handleSelectRace={handleSelectRace}
      isDriver={isDriver}
      trackDataSource={trackDataSource}
      ref={ref}
      isWPSBetType={isWPSBetType}
      emptyCommentsHandicapping={emptyCommentsHandicapping}
      emptyComments={emptyComments}
      handicapping={handicapping}
      height={height}
      loading={loading}
      timeform={timeform}
      changes={changes}
      hasSilk={hasSilk}
    >
      {children}
    </UIComponent>
  );
};

export default RunnerInfo;
